<script setup lang="ts">
import { Info, SendHorizonal, Smile, X } from '@lucide/vue';
import axios from 'axios';
import emojione from 'emojione';
import { computed, nextTick, onUnmounted, onUpdated, ref, watch } from 'vue';
import Country from '../domain/country/Country';
import PlayerName from '../domain/player/PlayerName';
import Channel from '../dto/Channel';
import ChannelMessageListGetResponse from '../dto/response/ChannelMessageListGetResponse';
import Mercure from '../js/component/Mercure';
import OtChatMessage from './ChatMessage.vue';
import OtEmojiPicker from './EmojiPicker.vue';
import OtIconButton from './IconButton.vue';
import { OtSelect, OtSelectContent, OtSelectGroup, OtSelectItem, OtSelectTrigger, OtSelectValue } from './select';
import { OtTextarea } from './textarea';

declare global {
    interface Window {
        emitter: any;
    }
}

interface Props {
    channel: number;
    locked?: boolean;
}
interface Emits {
    changeChannel: (channel: number) => void;
}

const props = withDefaults(defineProps<Props>(), {
    locked: false,
});
const emits = defineEmits<Emits>();

const channels = ref<Channel[]>([]);
const isBanned = ref(false);
const bannedMessage = ref('');
const message = ref('');
const showEmojiPicker = ref(false);
const messages = ref<ChannelMessageListGetResponse[]>([]);
// numbers history loads, so an older response can't override a newer one that was already applied
let latestLoad = 0;
let latestAppliedLoad = 0;

emojione.imageTitleTag = false;
emojione.ascii = true;

const contentContainer = ref(null);
const parsedMessage = computed(() => emojione.shortnameToUnicode(message.value));

watch(() => props.channel, (channel, previousChannel) => {
    messages.value = [];
    Mercure.unwatchChannel(previousChannel);
    Mercure.watchChannel(channel);
});
Mercure.watchChannel(props.channel);
onUnmounted(() => {
    Mercure.unwatchChannel(props.channel);
    window.emitter.off('chat-message', onChatMessage);
    window.emitter.off('chat-messages-stale', loadMessages);
});

if (!props.locked) {
    refreshChannels();
}
loadMessages();

window.emitter.on('chat-message', onChatMessage);
// messages the hub's replay cannot cover (no event id yet, newly subscribed channel) arrive only via the API
window.emitter.on('chat-messages-stale', loadMessages);
function onChatMessage(message) {
    if (message.channel_id !== props.channel) {
        return;
    }
    upsertMessage(new ChannelMessageListGetResponse(
        message.id,
        message.content,
        new Date(message.date),
        message.author_id,
        PlayerName.fromData(message.author_name),
        Country.fromData(message.author_country),
        message.author_is_online,
        message.author_is_mobile,
    ));
}

// live and replayed messages overlap and arrive in any order: dedupe by id and keep them sorted
function upsertMessage(incoming: ChannelMessageListGetResponse) {
    messages.value = [
        ...messages.value.filter(existing => existing.getId() !== incoming.getId()),
        incoming,
    ]
        .sort((a, b) => a.getId() - b.getId());
}
onUpdated(() => {
    scrollToBottom();
});

function channelName(channel: Channel) {
    if (!channel.name) {
        return '';
    }

    return `[${channel.numberOfVisitors}] ${channel.name}`;
}

function showChannelInfo() {
    window.location.href = Routing.generate('chat_channel_details', { _locale: locale, id: props.channel });
}

function postChatMessage() {
    if (message.value === '') {
        return;
    }

    axios.post(`/api/channels/${props.channel}/messages`, {
        message: emojione.toShort(message.value),
    });
    message.value = '';
}

function loadMessages() {
    const channel = props.channel;
    // an older response applied last could bring back a deleted message
    const load = ++latestLoad;
    const newestShownId = Math.max(0, ...messages.value.map(shown => shown.getId()));
    axios
        .get(`/api/channels/${channel}/messages`)
        .then((response) => {
            if (load < latestAppliedLoad || channel !== props.channel || !response.data || !response.data.data) {
                return;
            }
            latestAppliedLoad = load;
            // the server's latest messages are authoritative (e.g. deleted ones disappear, also when none are left);
            // keep only live messages that arrived during the request and are newer than the loaded ones
            const loaded: ChannelMessageListGetResponse[] = response.data.data.map(data => ChannelMessageListGetResponse.fromData(data));
            const newestKnownId = Math.max(newestShownId, ...loaded.map(loadedMessage => loadedMessage.getId()));
            messages.value = [
                ...loaded,
                ...messages.value.filter(existing => existing.getId() > newestKnownId),
            ];
        });
}

async function refreshChannels() {
    const response = await axios.get('/api/channels');
    channels.value = response.data.data.map(data => Channel.fromData(data));
    setTimeout(refreshChannels, 30000);
}

function scrollToBottom() {
    contentContainer.value.scrollTop = contentContainer.value.scrollHeight;
}

function connected(index, authorId) {
    if (!messages.value[index - 1]) {
        return false;
    }

    return authorId === messages.value[index - 1].getAuthorId();
}

function showDate(index, date) {
    if (!messages.value[index - 1]) {
        return true;
    }

    return date.toDateString() !== messages.value[index - 1].getDate().toDateString();
}

async function changeChannel(channel) {
    emits('changeChannel', channel);
    await nextTick();
    loadMessages();
}
</script>

<template>
    <div class="flex flex-col h-full divide-y divide-gray-500">
        <div
            v-if="!locked"
            class="p-2 flex gap-2"
        >
            <ot-select
                :model-value="channel.toString()"
                @update:model-value="changeChannel(parseInt($event))"
            >
                <ot-select-trigger>
                    <ot-select-value />
                </ot-select-trigger>
                <ot-select-content>
                    <ot-select-group>
                        <ot-select-item
                            v-for="channel in channels"
                            :key="channel.id"
                            :value="channel.id.toString()"
                        >
                            {{ channelName(channel) }}
                        </ot-select-item>
                    </ot-select-group>
                </ot-select-content>
            </ot-select>
            <ot-icon-button @click="showChannelInfo()">
                <info />
            </ot-icon-button>
        </div>
        <div ref="contentContainer" class="grow" style="overflow-y: auto">
            <div>
                <ot-chat-message
                    v-for="(message, index) in messages"
                    :key="message.getId()"
                    :message="message"
                    :connected="connected(index, message.getAuthorId())"
                    :show-date="showDate(index, message.getDate())"
                />
            </div>
        </div>
        <p v-if="isBanned">{{ bannedMessage }}</p>
        <div v-else>
            <ot-emoji-picker v-show="showEmojiPicker" @select="message += $event.native" />
            <div class="flex p-2 items-center gap-2">
                <ot-icon-button @click="showEmojiPicker = !showEmojiPicker">
                    <x v-if="showEmojiPicker" />
                    <smile v-if="!showEmojiPicker" />
                </ot-icon-button>
                <ot-textarea
                    v-if="!isBanned"
                    :model-value="parsedMessage"
                    :label="$t('general.message')"
                    auto-grow
                    @keydown.prevent.enter="postChatMessage"
                    @update:model-value="message = $event"
                />
                <ot-icon-button @click="postChatMessage">
                    <send-horizonal />
                </ot-icon-button>
            </div>
        </div>
    </div>
</template>
